import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const { lang } = useLanguage();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-screen flex items-center justify-center bg-[#050505] px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <Link
          to="/admin/login"
          data-testid="back-to-login-link"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#A19E9C] hover:text-[#C5A059] transition-colors mb-12"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={1.5} />
          {lang === "fr" ? "Retour à la connexion" : "Back to sign in"}
        </Link>

        <div className="mb-10">
          <p className="overline text-[#C5A059] mb-3">
            {lang === "fr" ? "Récupération" : "Recovery"}
          </p>
          <h1 className="text-3xl md:text-4xl tracking-tight font-serif text-[#F2EBE5]">
            {lang === "fr" ? "Mot de passe oublié ?" : "Forgot your password?"}
          </h1>
          <p className="mt-4 text-[13px] leading-relaxed text-[#A19E9C]">
            {lang === "fr"
              ? "Entrez votre adresse email. Si elle correspond à un compte, vous recevrez un lien pour réinitialiser votre mot de passe (valide 30 minutes)."
              : "Enter your email. If it matches an account, we'll send you a link to reset your password (valid 30 minutes)."}
          </p>
        </div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-testid="forgot-success"
            className="border border-[#C5A059]/30 bg-[#0a0a0a] p-6 flex items-start gap-4"
          >
            <CheckCircle2 className="w-5 h-5 text-[#C5A059] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-serif text-[#F2EBE5] mb-2">
                {lang === "fr" ? "Email envoyé" : "Email sent"}
              </p>
              <p className="text-[13px] leading-relaxed text-[#A19E9C]">
                {lang === "fr"
                  ? "Si un compte correspond à cette adresse, un email vient d'être envoyé. Vérifiez votre boîte de réception (et le dossier spam)."
                  : "If an account matches this address, an email was just sent. Check your inbox (and spam folder)."}
              </p>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-[10px] uppercase tracking-[0.3em] text-[#A19E9C] mb-2">
                {lang === "fr" ? "Adresse email" : "Email address"}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C5A059]" strokeWidth={1.5} />
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  className="kw-input pl-10"
                  placeholder="vous@exemple.com"
                />
              </div>
            </div>

            {error && (
              <p data-testid="forgot-error" className="text-sm text-red-400 border-l-2 border-red-400/40 pl-3">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || !email}
              data-testid="forgot-submit"
              className="kw-btn-primary w-full justify-center disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  {lang === "fr" ? "Envoi…" : "Sending…"}
                </>
              ) : (
                <>{lang === "fr" ? "Envoyer le lien" : "Send the link"}</>
              )}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
